// WS5 PR3.1 — Headbox AABB pure math.
//
// Tutte le funzioni sono pure (no side effects, no THREE dep): facilita
// unit test e riuso lato Studio Web admin per preview/validation.
//
// Convenzione coordinate: metri, viewer-frame (Y-up). Coerente con i centri
// splat post-pipeline (cfr. backend src/utils/viewbox-mapper.ts + worker
// oniri_worker/viewbox_manifest.py _max_xyz_to_viewer_meters).

import type { ModelViewbox, Vec3 } from "../api/types.js";
import { HEADBOX_EDGE_PADDING_M } from "./types.js";

/** AABB { min, max } in metri. */
export interface AABB {
	min: Vec3;
	max: Vec3;
}

/** Calcola min/max dato center + size (size invariante per traslazione). */
export function headboxMinMax(center: Vec3, size: Vec3): AABB {
	return {
		min: [
			center[0] - size[0] / 2,
			center[1] - size[1] / 2,
			center[2] - size[2] / 2,
		],
		max: [
			center[0] + size[0] / 2,
			center[1] + size[1] / 2,
			center[2] + size[2] / 2,
		],
	};
}

/** True se il punto e' dentro l'AABB. Per containment puntuale, padding NON
 *  applicato (e' diverso da clamp eye-with-padding). */
export function containsPoint(point: Vec3, headbox: ModelViewbox["headbox"]): boolean {
	return (
		point[0] >= headbox.min[0] &&
		point[0] <= headbox.max[0] &&
		point[1] >= headbox.min[1] &&
		point[1] <= headbox.max[1] &&
		point[2] >= headbox.min[2] &&
		point[2] <= headbox.max[2]
	);
}

/** Clamp della camera-eye position dentro l'AABB headbox con padding
 *  minimo per evitare contatto-muro perceptual.
 *
 *  Padding di default `HEADBOX_EDGE_PADDING_M` (10 cm). Per headbox
 *  troppo piccolo (size < 2*padding su uno qualsiasi degli assi), il
 *  padding viene ridotto a 0 su quell'asse (l'utente entra a contatto
 *  con il muro ma NON viene teletrasportato nel center, che sarebbe
 *  jarring). Edge case: headbox degenere (size <= 0) → return center
 *  (failsafe, ma ViewboxCallbackEntry validation backend gia' rifiuta
 *  size <= 0 a INSERT-time).
 *
 *  Coerente col walkTick orchestrator: dopo applyGravity(), prima di
 *  setCamera() → assicura che il movimento WASD/joystick resti nel
 *  volume calpestabile. */
export function clampEyeToHeadbox(
	eyePos: Vec3,
	headbox: ModelViewbox["headbox"],
	padding: number = HEADBOX_EDGE_PADDING_M,
): Vec3 {
	// Destructuring esplicito per evitare noUncheckedIndexedAccess fragility
	// (TS in strict mode tratta array[number] come T|undefined). Vec3 e' una
	// tuple readonly [number, number, number] → destructure produce number.
	const [px, py, pz] = eyePos;
	const [minX, minY, minZ] = headbox.min;
	const [maxX, maxY, maxZ] = headbox.max;
	return [
		clampAxis(px, minX, maxX, padding),
		clampAxis(py, minY, maxY, padding),
		clampAxis(pz, minZ, maxZ, padding),
	];
}

function clampAxis(v: number, min: number, max: number, padding: number): number {
	const size = max - min;
	const effectivePadding = size >= 2 * padding ? padding : 0;
	const lo = min + effectivePadding;
	const hi = max - effectivePadding;
	if (v < lo) return lo;
	if (v > hi) return hi;
	return v;
}

/** Verifica se un punto-target di click-to-move e' dentro l'AABB headbox
 *  (PR3.2 mutex con walk: se hit fuori AABB → no-op + toast educational).
 *  Y-axis controlla che il pavimento di destinazione sia dentro headbox
 *  (es. impedisce di click-to-move su scale tra due viewbox sovrapposte). */
export function targetInsideHeadbox(
	target: Vec3,
	headbox: ModelViewbox["headbox"],
): boolean {
	return containsPoint(target, headbox);
}

/** Calcola il centro geometrico (medio) dell'AABB. Utile per camera fit
 *  di overview o spawn fallback quando default_camera.pos non e' valido. */
export function headboxCenter(headbox: ModelViewbox["headbox"]): Vec3 {
	return [
		(headbox.min[0] + headbox.max[0]) / 2,
		(headbox.min[1] + headbox.max[1]) / 2,
		(headbox.min[2] + headbox.max[2]) / 2,
	];
}

/** Volume dell'AABB (m^3). Usato per warning admin Studio Web se viewbox
 *  inverosimile (< 1 m^3 = bug parse; > 50 m^3 = forse non interior).
 *  Vedi PLAN §13 edge case. */
export function headboxVolume(headbox: ModelViewbox["headbox"]): number {
	const dx = headbox.max[0] - headbox.min[0];
	const dy = headbox.max[1] - headbox.min[1];
	const dz = headbox.max[2] - headbox.min[2];
	return Math.max(0, dx) * Math.max(0, dy) * Math.max(0, dz);
}
